<template>
  <section class="admin-content">
    <Navbar />
    <main class="admin-main">

      <!-- ── Page Header ── -->
      <div class="page-header">
        <div class="container">
          <div class="d-flex flex-wrap align-items-center text-white" style="padding: 32px 0; gap: 16px;">
            <div class="header-icon-wrap">
              <i class="fe fe-edit-2" style="font-size: 22px;"></i>
            </div>
            <div class="flex-grow-1">
              <small class="text-uppercase font-weight-bold" style="opacity: .75; letter-spacing: .08em;">Edit HMO / TPA</small>
              <h4 class="mb-0 font-weight-bold">{{ original.organization_name || '…' }}</h4>
            </div>
            <router-link :to="'/tpa/' + $route.params.id" class="btn btn-outline-light btn-sm font-weight-bold">
              <i class="fe fe-arrow-left mr-1"></i> Back
            </router-link>
          </div>
        </div>
      </div>

      <div class="container mt-n3 pb-5">
        <div v-if="!canManage" class="card action-card shadow-sm">
          <div class="card-body text-center text-muted py-5">
            <i class="fe fe-lock d-block mb-2" style="font-size: 2rem;"></i>
            Only agency admins can edit an HMO / TPA.
          </div>
        </div>

        <div v-else-if="notFound" class="card action-card shadow-sm">
          <div class="card-body text-center text-muted py-5">
            <i class="fe fe-alert-circle d-block mb-2" style="font-size: 2rem;"></i>
            This HMO / TPA could not be found.
            <div class="mt-3"><router-link to="/my-tpas" class="btn btn-sm btn-outline-primary">Back to the list</router-link></div>
          </div>
        </div>

        <div v-else class="card form-card shadow">
          <div class="form-card-header">
            <div class="section-number">1</div>
            <h6 class="mb-0 font-weight-bold text-white">Organisation details</h6>
          </div>
          <div class="card-body px-4 pt-4 pb-3">
            <form @submit.prevent="save">
              <div class="form-row">
                <div class="form-group col-md-12">
                  <label for="tpa-name" class="small font-weight-bold text-secondary">
                    Organisation name <span class="text-danger">*</span>
                  </label>
                  <input id="tpa-name" type="text" required maxlength="191" class="form-control"
                    :class="{ 'is-invalid': errors.organization_name }" v-model.trim="form.organization_name" />
                  <div v-if="errors.organization_name" class="invalid-feedback">{{ errors.organization_name[0] }}</div>
                </div>
                <div class="form-group col-md-6">
                  <label for="tpa-email" class="small font-weight-bold text-secondary">
                    Email <span class="text-danger">*</span>
                  </label>
                  <input id="tpa-email" type="email" required maxlength="191" class="form-control"
                    :class="{ 'is-invalid': errors.email }" v-model.trim="form.email" />
                  <div v-if="errors.email" class="invalid-feedback">{{ errors.email[0] }}</div>
                </div>
                <div class="form-group col-md-6">
                  <label for="tpa-phone" class="small font-weight-bold text-secondary">
                    Phone number <span class="text-danger">*</span>
                  </label>
                  <input id="tpa-phone" v-phone type="text" required class="form-control" placeholder="e.g. 08012345678"
                    :class="{ 'is-invalid': errors.phone_number }" v-model="form.phone_number" />
                  <div v-if="errors.phone_number" class="invalid-feedback">{{ errors.phone_number[0] }}</div>
                </div>
                <div class="form-group col-md-6">
                  <label for="tpa-code" class="small font-weight-bold text-secondary">HMO Code</label>
                  <input id="tpa-code" type="text" maxlength="191" class="form-control" placeholder="e.g. HYG-01"
                    :class="{ 'is-invalid': errors.tpa_id }" v-model.trim="form.tpa_id" />
                  <div v-if="errors.tpa_id" class="invalid-feedback">{{ errors.tpa_id[0] }}</div>
                  <small v-else class="form-text text-muted">Printed on enrollees' ID cards.</small>
                </div>
                <div class="form-group col-md-12">
                  <label for="tpa-address" class="small font-weight-bold text-secondary">Office Address</label>
                  <textarea id="tpa-address" rows="2" maxlength="1000" class="form-control"
                    placeholder="e.g. 12 Herbert Macaulay Way, Wuse, Abuja"
                    :class="{ 'is-invalid': errors.address }" v-model.trim="form.address"></textarea>
                  <div v-if="errors.address" class="invalid-feedback">{{ errors.address[0] }}</div>
                </div>
                <div class="form-group col-md-6">
                  <label for="tpa-status" class="small font-weight-bold text-secondary">Status</label>
                  <select id="tpa-status" class="form-control" v-model="form.org_status">
                    <option :value="1">Active</option>
                    <option :value="0">Inactive</option>
                  </select>
                  <small class="form-text text-muted">Inactive HMOs are hidden where only active ones are listed.</small>
                </div>
              </div>

              <p class="small text-muted mb-3">
                <i class="fe fe-info mr-1"></i>
                These are the organisation's details. The HMO's sign-in email and password are not changed here.
              </p>

              <div class="d-flex justify-content-end" style="gap: 10px;">
                <router-link :to="'/tpa/' + $route.params.id" class="btn btn-outline-secondary px-4">Cancel</router-link>
                <button type="submit" class="btn btn-submit px-5" :disabled="saving || !changed">
                  <i class="fe fe-save mr-2"></i> {{ saving ? 'Saving…' : 'Save changes' }}
                </button>
              </div>
            </form>
          </div>
        </div>
      </div>

      <div class="vld-parent">
        <loading :active.sync="isLoading" loader="dots" :can-cancel="true" :is-full-page="true"></loading>
      </div>
    </main>
  </section>
</template>

<script>
import Navbar from "@/views/Navbar.vue";
import Loading from "vue-loading-overlay";
import "vue-loading-overlay/dist/vue-loading.css";

const FIELDS = ["organization_name", "email", "phone_number", "tpa_id", "address", "org_status"];

export default {
  components: {
    Navbar,
    Loading,
  },
  data() {
    return {
      user: JSON.parse(localStorage.getItem("user")),
      original: {},
      form: { organization_name: "", email: "", phone_number: "", tpa_id: "", address: "", org_status: 1 },
      errors: {},
      notFound: false,
      isLoading: false,
      saving: false,
    };
  },
  computed: {
    canManage() {
      return !!this.user && (this.user.type === "shis" || Number(this.user.user_role) === 1);
    },
    changed() {
      const text = (v) => (v === null || v === undefined ? "" : String(v));
      return FIELDS.some((f) => text(this.form[f]) !== text(this.original[f]));
    },
  },
  created() {
    if (this.canManage) this.load();
  },
  methods: {
    load() {
      this.isLoading = true;
      this.axios
        .get(`/api/v1/auth/orgenrollment/${this.$route.params.id}`)
        .then((response) => {
          if (!response.data) {
            this.notFound = true;
            return;
          }
          this.setFrom(response.data);
        })
        .catch((error) => {
          console.error(error);
          this.notFound = true;
        })
        .finally(() => {
          this.isLoading = false;
        });
    },
    setFrom(tpa) {
      const status = tpa.org_status === null || tpa.org_status === undefined ? 1 : Number(tpa.org_status);
      this.original = Object.assign({}, tpa, { org_status: status });
      this.form = {
        organization_name: tpa.organization_name || "",
        email: tpa.email || "",
        phone_number: tpa.phone_number || "",
        tpa_id: tpa.tpa_id || "",
        address: tpa.address || "",
        org_status: status,
      };
    },
    save() {
      this.saving = true;
      this.errors = {};
      this.axios
        .put(`/api/v1/auth/orgenrollment/${this.$route.params.id}`, this.form)
        .then((response) => {
          if (response.data && response.data.data) this.setFrom(response.data.data);
          this.$toasted.success("HMO / TPA updated", { position: "top-center", duration: 3000 });
          this.$router.push(`/tpa/${this.$route.params.id}`);
        })
        .catch((error) => {
          const data = (error.response && error.response.data) || {};
          this.errors = data.errors || {};
          this.$toasted.error(data.message || "Could not save the changes", { position: "top-center", duration: 4000 });
        })
        .finally(() => {
          this.saving = false;
        });
    },
  },
};
</script>
